import Vue from 'vue';
import { Events, MessageState, ChatType } from 'Models/ChatModel';
import ChatMessage from 'Root/ws/classes/ChatMessage.class';
import ClientTypedChat from 'Root/classes/ClientTypedChat';
/**
 * Класс комнаты чата на клиенте
 */
class ClientChatRoom {
  /**
   * @param roomId {string}
   * @param chat {ClientChat}
   */
  constructor(roomId, chat) {
    this.id = roomId;
    this.users = {};
    this.chat = chat;
    this.messages = {};
    this.typedChats = {
      [ChatType.SUPPORT.id]: new ClientTypedChat(this, ChatType.SUPPORT.id),
      [ChatType.PRO.id]: new ClientTypedChat(this, ChatType.PRO.id),
    };
    this._joined = false;

    this.join();
  }

  /**
   * Возвращает интерефейс для чата customer - pro
   * @returns {ClientTypedChat}
   */
  get proChat() {
    return this.typedChats[ChatType.PRO.id];
  }

  /**
   * Возвращает интерефейс для чата customer - support
   * @returns {ClientTypedChat}
   */
  get supportChat() {
    return this.typedChats[ChatType.SUPPORT.id];
  }

  /**
   * Добавляет сообщение в список
   * @param message {ChatMessage} - Объект сообщения
   */
  addMessage(message) {
    if (!message.date) {
      message.date = Date.now();
    }

    Vue.set(this.messages, message.hash, {
      ...message,
    });
  }

  getUsers() {
    this.chat.emitToChat(Events.GET_USERS_IN_ROOM, this.id);
  }

  /**
   * Устанавливает список пользователей для чата
   * @param users {Object}
   */
  setUsers(users) {
    Vue.set(this, 'users', users);
  }

  /**
   * Исключает юзера из комнаты
   * @param userId {Number}
   */
  removeUser(userId) {
    Vue.delete(this.users, userId);
  }

  /**
   * Выполняет подключение к комнате чата
   */
  join() {
    this.chat.emitToChat(Events.JOIN, this.id);
    this._joined = true;
  }

  /**
   * Выполняет отключение от комнаты чата
   */
  leave() {
    this.chat.emitToChat(Events.LEAVE, this.id);
    this._joined = false;
  }

  /**
   * Отправляет текстовое сообщение в комнату чата
   * @param text {string} - Текст сообщения
   * @param parentOrderId {string?} - ID родительского заказа
   * @param type {Number} - Тип чата ChatModel.ChatType
   */
  sendMessage(text, parentOrderId, type) {
    const message = new ChatMessage({
      roomId: this.id,
      text: text.trim(),
      type,
      senderId: this.chat.user.id,
      hash: `${Date.now()}-${this.chat.user.id}`,
      state: MessageState.SENDING.id,
      parentOrderId,
    });
    this.chat.emitToChat(Events.MESSAGE, message);
    this.addMessage({ ...message, user: this.chat.user });
  }

  /**
   * Делает запрос на получение истории комнаты чата
   * Все параметны необязательные.
   * Если type не был передан - вернется история для всех типов чатов.
   * Если limit не был передан - вернутся все непрочитанные сообщения + 5
   * @param type {Number}
   * @param offset {Number}
   * @param limit {Number}
   */
  getHistory(type, offset = 0, limit) {
    this.chat.emitToChat(Events.GET_HISTORY, {
      roomId: this.id,
      type,
      offset,
      limit,
    });
  }

  /**
   * Устанавливает состояние для сообщения
   * @param messageHash {string} - Hash сообщения
   * @param state {Number} - ID остояния сообщения ChatModel.MessageState
   * @param isOk {Boolean} - True если сообщение не содержит стоп-слов
   */
  setMessageState(messageHash, state, isOk = true) {
    if (this.messages[messageHash]) {
      this.messages[messageHash].state = state;
      this.messages[messageHash].isOk = isOk;
    }
  }

  /**
   * Помечает сообщение как прочитанное и сохраняет в редис
   * @param messageHash {string} - Hash сообщения
   * @param roomId {Number} - ID комнаты
   * @param type {number} - тип сообщений ChatModel.ChatType
   */
  markMessageAsRead(messageHash, roomId, type) {
    this.setMessageState(messageHash, MessageState.READ.id);
    this.chat.emitToChat(Events.MESSAGE_STATE, {
      messageHash,
      roomId,
      state: MessageState.READ.id,
      type,
    });
  }

  clearMessages() {
    // Эта штука дёргает watch: messages -> Chat.vue
    Vue.set(this, 'messages', {});
  }
}

export default ClientChatRoom;
